import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  Spinner,
  Button,
  Carousel,
  Collapse,
  Form,
} from "react-bootstrap";

import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";

import { getProductById } from "./api";
import { mapAsplProduct } from "./mapProduct";

const AsplDetailPage = () => {
  const { id } = useParams();

  const [product, setProduct] = useState(null);
  const [fitments, setFitments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [expandedFitments, setExpandedFitments] = useState({});
  const [showFitmentSearch, setShowFitmentSearch] = useState(false);
  const [fitmentSearchTerm, setFitmentSearchTerm] = useState("");
  const referenceNumbers = Array.isArray(product?.referenceNumbers) ? product.referenceNumbers : [];

  useEffect(() => {
    const loadProduct = async () => {
      setLoading(true);
      try {
        const res = await getProductById(id);
        const info =
          res.data.getProductsInfoResponse?.productInfoList?.productInfo?.[0];

        const mapped = mapAsplProduct(info);
        setProduct(mapped);
        const allFitments = Array.isArray(mapped?.fitments)
          ? mapped.fitments
          : (mapped?.fitment ? [mapped.fitment] : []);
        setFitments(allFitments);
      } catch (err) {
        console.error("Failed to load ASPL product", err);
        setFitments([]);
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [id]);

  /* ---------------------------------------------------
     WHATSAPP BUY HANDLER
  --------------------------------------------------- */
  const handleWhatsApp = () => {
    if (!product) return;

    const productUrl = window.location.href;

    const message = `
Hello! I'm interested in this ASPL auto part:

🛠 *${product.name}*
🏷 *Brand:* ${product.brand}
📦 *Category:* ${product.category}

💵 *Price:* ₦${Number(product.priceNGN).toLocaleString("en-NG")}
💲 *USD:* $${product.priceUSD}
🔁 *Exchange Rate:* ₦${product.exchangeRate}/$

📌 *Reference:* ${product.reference}
📌 *OEM:* ${product.oem}
🚚 *Delivery Date:* ${product.deliveryDate}

🔗 Product Link: ${productUrl}
    `.trim();

    const encoded = encodeURIComponent(message);
    const whatsappNumber = "2349098370120"; // your WhatsApp number

    window.open(`https://wa.me/${whatsappNumber}?text=${encoded}`, "_blank");
  };

  /* ---------------------------------------------------
     COPY PRODUCT URL
  --------------------------------------------------- */
  const handleCopyURL = () => {
    navigator.clipboard.writeText(window.location.href);
    alert("Product URL copied!");
  };

  if (loading || !product) {
    return (
      <>
        <Navbar />
        <Container className="py-5 text-center">
          <Spinner animation="border" />
        </Container>
        <Footer />
      </>
    );
  }

  const totalImages = product.photos.length;
  const filteredFitments = fitments
    .map((fitment, originalIndex) => ({ fitment, originalIndex }))
    .filter(({ fitment }) => {
      const query = fitmentSearchTerm.trim().toLowerCase();
      if (!query) return true;
      const searchable = [
        fitment.product_code,
        fitment.mark,
        fitment.model,
        fitment.series,
        fitment.engine_code,
        fitment.engine_type,
        fitment.date_from,
        fitment.date_to,
        fitment.capacity,
        fitment.hp,
        fitment.kw,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return searchable.includes(query);
    });

  const toggleFitmentDetails = (index) => {
    setExpandedFitments((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  return (
    <>
      <Navbar />

      <Container className="py-5">
        <Card className="shadow border-0">
          <Row className="g-0">
            {/* IMAGES */}
            <Col md={12} lg={5}>
              <div className="p-3 p-md-4 border-bottom">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <small className="text-muted fw-semibold text-uppercase" style={{ letterSpacing: "0.04em" }}>
                    Product Images
                  </small>
                  <Badge bg="secondary">
                    {totalImages > 0 ? `Image ${carouselIndex + 1} of ${totalImages}` : "No Image"}
                  </Badge>
                </div>

                <Carousel
                  activeIndex={carouselIndex}
                  onSelect={setCarouselIndex}
                  controls={totalImages > 1}
                  indicators={true}
                  interval={null}
                >
                  {totalImages ? (
                    product.photos.map((img, i) => (
                      <Carousel.Item key={i}>
                        <img
                          src={img}
                          alt={`${product.name} ${i + 1}`}
                          className="d-block w-100"
                          style={{ maxHeight: 420, objectFit: "contain" }}
                        />
                      </Carousel.Item>
                    ))
                  ) : (
                    <Carousel.Item>
                      <div className="bg-light" style={{ height: 420 }} />
                    </Carousel.Item>
                  )}
                </Carousel>

                {totalImages > 1 && (
                  <div className="d-flex flex-wrap gap-2 mt-3">
                    {product.photos.map((_, i) => (
                      <Button
                        key={`jump-image-${i}`}
                        size="sm"
                        variant={carouselIndex === i ? "dark" : "outline-dark"}
                        onClick={() => setCarouselIndex(i)}
                      >
                        {i + 1}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            </Col>

            {/* INFO */}
            <Col md={12} lg={7}>
              <Card.Body className="p-4 p-lg-5">
                <h4>{product.name}</h4>

                <div className="mb-2">
                  <Badge bg="dark" className="me-2">
                    {product.brand}
                  </Badge>
                  <Badge bg={product.stock > 0 ? "success" : "danger"}>
                    {product.stock > 0 ? "In Stock" : "Out of Stock"}
                  </Badge>
                </div>

                <h5 className="text-success">
                  ₦{Number(product.priceNGN).toLocaleString("en-NG")}
                </h5>

                <div className="text-muted small mb-3">
                  USD: ${product.priceUSD}
                  <br />
                  Exchange Rate: ₦{product.exchangeRate}/$
                </div>

                <div
                  className="mt-3 p-3 rounded-3"
                  style={{
                    background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
                    border: "1px solid #e2ebfa",
                  }}
                >

                  <Row className="g-2">
                    {[
                      { label: "Reference", value: product.reference || "-" },
                      { label: "OEM", value: product.oem || "-" },
                      { label: "Tecnum", value: product.tecnum || "-" },
                      { label: "TecID", value: product.tecidd || "-" },
                      { label: "Category", value: product.category || "-", full: true },
                      { label: "Delivery", value: product.deliveryDate || "-", full: true },
                    ].map((item) => (
                      <Col xs={12} md={item.full ? 12 : 6} key={item.label}>
                        <div
                          className="h-100 p-2 rounded-3"
                          style={{
                            backgroundColor: item.full ? "#f2f7ff" : "#ffffff",
                            border: "1px solid #e3ecfa",
                            minHeight: item.full ? 68 : 64,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                          }}
                        >
                          <div className="text-uppercase small" style={{ color: "#7287a6", fontSize: "0.7rem", letterSpacing: "0.04em" }}>
                            {item.label}
                          </div>
                          <div className="fw-semibold" style={{ color: "#1f2d3d", wordBreak: "break-word" }}>
                            {item.value}
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </div>

                <div className="mt-4 d-flex gap-3 flex-wrap">
                  <Button variant="success" onClick={handleWhatsApp}>
                    Buy on WhatsApp
                  </Button>

                  <Button variant="outline-dark" onClick={handleCopyURL}>
                    Copy Product URL
                  </Button>
                </div>
              </Card.Body>
            </Col>
          </Row>
        </Card>

        {referenceNumbers.length > 0 && (
          <Card
            className="shadow border-0 mt-4"
            style={{
              background: "linear-gradient(180deg, #f8fbff 0%, #f3f7ff 100%)",
              borderColor: "#dfe6f5",
            }}
          >
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <h5 className="mb-0 text-uppercase" style={{ letterSpacing: "0.04em", color: "#16325c" }}>
                  Reference Numbers
                </h5>
                <Badge bg="primary">{referenceNumbers.length} Match(es)</Badge>
              </div>
              <div className="d-flex flex-wrap gap-2">
                {referenceNumbers.map((ref, index) => (
                  <Link
                    key={`${ref.ref_no}-${index}`}
                    to={`/aspl/product/${encodeURIComponent(ref.ref_no)}`}
                    className="text-decoration-none"
                  >
                    <Badge
                      bg="light"
                      text="dark"
                      className="px-3 py-2 border"
                      style={{ borderColor: "#dbe7ff", cursor: "pointer" }}
                    >
                      <span className="fw-semibold">{ref.ref_no}</span>
                      {ref.ref_producer ? ` · ${ref.ref_producer}` : ""}
                    </Badge>
                  </Link>
                ))}
              </div>
            </Card.Body>
          </Card>
        )}

        {fitments.length > 0 && (
          <Card
            className="shadow border-0 mt-4"
            style={{
              background: "linear-gradient(180deg, #f8fbff 0%, #f3f7ff 100%)",
              borderColor: "#dfe6f5",
            }}
          >
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <h5 className="mb-0 text-uppercase" style={{ letterSpacing: "0.04em", color: "#16325c" }}>
                  Vehicle Fitment Data
                </h5>
                <Badge bg="primary">{fitments.length} Match(es)</Badge>
              </div>

              <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <Button
                  size="sm"
                  variant={showFitmentSearch ? "dark" : "outline-dark"}
                  onClick={() => setShowFitmentSearch((prev) => !prev)}
                >
                  We found {fitments.length} matches. Do you want to search a particular fitment?
                </Button>
              </div>

              <Collapse in={showFitmentSearch}>
                <div>
                  <Form.Group className="mb-3">
                    <Form.Control
                      type="text"
                      placeholder="Type make, model, series, engine, date, HP, kW..."
                      value={fitmentSearchTerm}
                      onChange={(e) => setFitmentSearchTerm(e.target.value)}
                    />
                    <Form.Text className="text-muted">
                      Showing {filteredFitments.length} of {fitments.length} match(es).
                    </Form.Text>
                  </Form.Group>
                </div>
              </Collapse>

              <div className="d-flex flex-column gap-3">
                {filteredFitments.map(({ fitment, originalIndex }, idx) => (
                  <div
                    key={`${fitment.product_code || "fit"}-${originalIndex}`}
                    className="p-3 rounded-3"
                    style={{ backgroundColor: "#eef4ff", border: "1px solid #dbe7ff" }}
                  >
                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
                      <div>
                        <div className="small fw-semibold text-primary">Match #{idx + 1}</div>
                        <div className="small text-muted">
                          Matched by: {fitment._source_value || fitment._matched_code || "-"}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant={expandedFitments[originalIndex] ? "dark" : "outline-dark"}
                        onClick={() => toggleFitmentDetails(originalIndex)}
                        aria-controls={`fitment-details-${originalIndex}`}
                        aria-expanded={Boolean(expandedFitments[originalIndex])}
                      >
                        {expandedFitments[originalIndex] ? "View Less" : "View More"}
                      </Button>
                    </div>

                    <Row className="g-2 mb-2">
                      {[
                        { label: "Make", value: fitment.mark || "-" },
                        { label: "Model", value: fitment.model || "-" },
                        { label: "Series", value: fitment.series || "-" },
                      ].map((item) => (
                        <Col xs={12} md={4} key={`${item.label}-${originalIndex}-summary`}>
                          <div
                            className="h-100 p-2 rounded-3"
                            style={{ backgroundColor: "#ffffff", border: "1px solid #e4ecfa", minHeight: 76 }}
                          >
                            <div className="text-uppercase small" style={{ color: "#6e7f9c", fontSize: "0.7rem", letterSpacing: "0.04em" }}>
                              {item.label}
                            </div>
                            <div className="fw-semibold" style={{ color: "#1f2d3d", wordBreak: "break-word" }}>
                              {item.value}
                            </div>
                          </div>
                        </Col>
                      ))}
                    </Row>

                    <Collapse in={Boolean(expandedFitments[originalIndex])}>
                      <div id={`fitment-details-${originalIndex}`}>
                        <Row className="g-2 mt-1">
                          {[
                            { label: "Product Code", value: fitment.product_code || product.reference },
                            { label: "Engine Code", value: fitment.engine_code || "-" },
                            { label: "Engine Type", value: fitment.engine_type || "-" },
                            { label: "Date From", value: fitment.date_from || "-" },
                            { label: "Date To", value: fitment.date_to || "-" },
                            { label: "Capacity", value: fitment.capacity || "-" },
                            { label: "HP", value: fitment.hp || "-" },
                            { label: "kW", value: fitment.kw || "-" },
                          ].map((item) => (
                            <Col xs={12} sm={6} lg={3} key={`${item.label}-${originalIndex}-detail`}>
                              <div
                                className="h-100 p-2 rounded-3"
                                style={{ backgroundColor: "#ffffff", border: "1px solid #e4ecfa", minHeight: 76 }}
                              >
                                <div className="text-uppercase small" style={{ color: "#6e7f9c", fontSize: "0.7rem", letterSpacing: "0.04em" }}>
                                  {item.label}
                                </div>
                                <div className="fw-semibold" style={{ color: "#1f2d3d", wordBreak: "break-word" }}>
                                  {item.value}
                                </div>
                              </div>
                            </Col>
                          ))}
                        </Row>
                      </div>
                    </Collapse>
                  </div>
                ))}
              </div>
              {filteredFitments.length === 0 && (
                <div className="text-center text-muted py-3 border rounded-3 bg-white">
                  No fitment result matches your search term.
                </div>
              )}
            </Card.Body>
          </Card>
        )}
      </Container>

      <Footer />
    </>
  );
};

export default AsplDetailPage;
